import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Settings, Users, Building2, Mail, Phone, MapPin, 
  DollarSign, Save, Plus, Edit, Trash2, Shield, UserPlus, X,
  MessageCircle, GripVertical, Clock
} from 'lucide-react';

const UserModal = ({ isOpen, onClose, user, onSave }) => {
  const { isSuperAdmin } = useAuth();
  const [formData, setFormData] = useState({
    email: '', password: '', first_name: '', last_name: '', role: 'sales',
  });

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || '',
        password: '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        role: user.role || 'sales',
      });
    } else {
      setFormData({
        email: '', password: '', first_name: '', last_name: '', role: 'sales',
      });
    }
  }, [user]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData, !!user);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-[#0A1628]">
            {user ? 'Edit User' : 'Add User'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>First Name *</Label>
              <Input
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                required
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Last Name *</Label>
              <Input
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                required
                className="rounded-xl"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Email *</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              disabled={!!user}
              className="rounded-xl"
            />
          </div>

          {!user && (
            <div className="space-y-2">
              <Label>Password *</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="rounded-xl"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>Role</Label>
            <Select 
              value={formData.role} 
              onValueChange={(v) => setFormData({ ...formData, role: v })}
              disabled={!isSuperAdmin && user?.role === 'admin'}
            >
              <SelectTrigger className="rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="viewer">Viewer</SelectItem>
                <SelectItem value="sales">Sales</SelectItem>
                {isSuperAdmin && <SelectItem value="admin">Admin</SelectItem>}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-xl">
              Cancel
            </Button>
            <Button type="submit" className="bg-[#014DB7] hover:bg-[#003A8C] text-white rounded-xl">
              {user ? 'Update User' : 'Create User'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export const AdminSettingsPage = () => {
  const { api, isSuperAdmin, user: currentUser } = useAuth();
  const [settings, setSettings] = useState({
    company_name: '',
    company_email: '',
    company_phone: '',
    company_address: '',
    invoice_prefix: 'INV',
    invoice_next_number: 1001,
    currency: 'USD',
    stripe_enabled: false,
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchData = async () => {
    try {
      const [settingsRes, usersRes] = await Promise.all([
        api.get('/api/settings/system'),
        api.get('/api/users'),
      ]);
      setSettings({ ...settings, ...settingsRes.data });
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/api/settings/system', settings);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveUser = async (data, isEdit) => {
    try {
      if (isEdit) {
        await api.put(`/api/users/${selectedUser.id}`, data);
        toast.success('User updated');
      } else {
        await api.post('/api/users', data);
        toast.success('User created');
      }
      setUserModalOpen(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleDeleteUser = async (user) => {
    if (user.is_protected) {
      toast.error('Cannot delete protected user');
      return;
    }
    if (!window.confirm(`Delete user ${user.first_name} ${user.last_name}?`)) return;
    
    try {
      await api.delete(`/api/users/${user.id}`);
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleToggleActive = async (user) => {
    if (user.is_protected) {
      toast.error('Cannot modify protected user');
      return;
    }
    try {
      await api.put(`/api/users/${user.id}`, { is_active: !user.is_active });
      toast.success(`User ${user.is_active ? 'deactivated' : 'activated'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update user');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#014DB7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#0A1628]">Admin Settings</h1>
        <p className="text-gray-500">Manage system configuration and users</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company Settings */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#014DB7]/10 rounded-xl flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#014DB7]" />
            </div>
            <h2 className="text-lg font-semibold text-[#0A1628]">Company Information</h2>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Company Name</Label>
              <Input
                value={settings.company_name}
                onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={settings.company_email || ''}
                onChange={(e) => setSettings({ ...settings, company_email: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={settings.company_phone || ''}
                onChange={(e) => setSettings({ ...settings, company_phone: e.target.value })}
                className="rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={settings.company_address || ''}
                onChange={(e) => setSettings({ ...settings, company_address: e.target.value })}
                className="rounded-xl"
              />
            </div>
          </div>
        </div>

        {/* Invoice Settings */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#84CC16]/10 rounded-xl flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-[#84CC16]" />
            </div>
            <h2 className="text-lg font-semibold text-[#0A1628]">Invoice Settings</h2>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Invoice Prefix</Label>
                <Input
                  value={settings.invoice_prefix}
                  onChange={(e) => setSettings({ ...settings, invoice_prefix: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Next Number</Label>
                <Input
                  type="number"
                  value={settings.invoice_next_number}
                  onChange={(e) => setSettings({ ...settings, invoice_next_number: parseInt(e.target.value) })}
                  className="rounded-xl"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select value={settings.currency} onValueChange={(v) => setSettings({ ...settings, currency: v })}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                  <SelectItem value="GBP">GBP (£)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-200">
              <p className="text-sm text-yellow-800">
                <strong>Stripe Integration:</strong> Ready for Stripe payment gateway. 
                Configure your Stripe keys to enable online payments.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Save Settings Button */}
      <div className="flex justify-end">
        <Button onClick={handleSaveSettings} disabled={saving} className="bg-[#014DB7] hover:bg-[#003A8C] text-white rounded-xl">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      {/* User Management */}
      <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5 text-purple-600" />
            </div>
            <h2 className="text-lg font-semibold text-[#0A1628]">User Management</h2>
          </div>
          <Button onClick={() => { setSelectedUser(null); setUserModalOpen(true); }} className="bg-[#014DB7] text-white rounded-xl">
            <UserPlus className="w-4 h-4 mr-2" /> Add User
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">User</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Email</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Role</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Chat</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-[#014DB7] to-[#84CC16] rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-medium">
                          {user.first_name?.[0]}{user.last_name?.[0]}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-[#0A1628]">{user.first_name} {user.last_name}</p>
                        {user.is_protected && (
                          <span className="text-xs text-yellow-600 flex items-center gap-1">
                            <Shield className="w-3 h-3" /> Protected
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-gray-600">{user.email}</td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      user.role === 'super_admin' ? 'bg-yellow-100 text-yellow-700' :
                      user.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {user.role?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      user.chat_available ? 'bg-[#84CC16]/20 text-[#65a30d]' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {user.chat_available ? '● Online' : 'Offline'}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-end gap-2">
                      {!user.is_protected && (
                        <>
                          <button
                            onClick={() => handleToggleActive(user)}
                            className="p-1.5 text-gray-400 hover:text-[#014DB7] rounded"
                            title={user.is_active ? 'Deactivate' : 'Activate'}
                          >
                            <Shield className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => { setSelectedUser(user); setUserModalOpen(true); }}
                            className="p-1.5 text-gray-400 hover:text-[#014DB7] rounded"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user)}
                            className="p-1.5 text-gray-400 hover:text-red-500 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {user.is_protected && (
                        <span className="text-xs text-gray-400">Cannot modify</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <UserModal
        isOpen={userModalOpen}
        onClose={() => { setUserModalOpen(false); setSelectedUser(null); }}
        user={selectedUser}
        onSave={handleSaveUser}
      />

      {/* Chat Round Robin */}
      <ChatRoundRobin api={api} />
    </div>
  );
};

// Chat Round Robin Component
const ChatRoundRobin = ({ api }) => {
  const [chatReps, setChatReps] = useState([]);
  const [currentPosition, setCurrentPosition] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);

  const fetchChatReps = async () => {
    try {
      const res = await api.get('/api/settings/chat-reps');
      setChatReps(res.data.reps || []);
      setCurrentPosition(res.data.current_position || 0);
    } catch (error) {
      console.error('Failed to fetch chat reps:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChatReps();
  }, []);

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;
    
    const newReps = [...chatReps];
    const draggedRep = newReps[draggedItem];
    newReps.splice(draggedItem, 1);
    newReps.splice(index, 0, draggedRep);
    
    setChatReps(newReps);
    setDraggedItem(index);
  };

  const handleDragEnd = async () => {
    setDraggedItem(null);
    setSaving(true);
    
    try {
      await api.put('/api/settings/chat-reps/order', {
        rep_order: chatReps.map(r => r.id)
      });
      toast.success('Chat queue order updated');
    } catch (error) {
      toast.error('Failed to update order');
      fetchChatReps(); // Revert on error
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
        <div className="flex items-center justify-center h-32">
          <div className="w-6 h-6 border-2 border-[#84CC16] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#84CC16]/10 rounded-xl flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-[#84CC16]" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[#0A1628]">Chat Round Robin</h2>
            <p className="text-sm text-gray-500">Drag to reorder chat assignment queue</p>
          </div>
        </div>
        {saving && (
          <span className="text-sm text-gray-500 flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-[#84CC16] border-t-transparent rounded-full animate-spin" />
            Saving...
          </span>
        )}
      </div>

      {chatReps.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-2">No chat reps available</p>
          <p className="text-sm text-gray-400">
            Users need to enable "Chat Available" in their profile to appear here
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {chatReps.map((rep, index) => (
              <div
                key={rep.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-move transition-all ${
                  index === currentPosition 
                    ? 'border-[#84CC16] bg-[#84CC16]/5' 
                    : 'border-gray-200 bg-white hover:border-gray-300'
                } ${draggedItem === index ? 'opacity-50' : ''}`}
              >
                <div className="text-gray-400 hover:text-gray-600">
                  <GripVertical className="w-5 h-5" />
                </div>
                
                <div className="flex items-center gap-2">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    index === currentPosition 
                      ? 'bg-[#84CC16] text-white' 
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {index + 1}
                  </span>
                </div>

                <div className="w-10 h-10 bg-gradient-to-br from-[#014DB7] to-[#84CC16] rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {rep.first_name?.[0]}{rep.last_name?.[0]}
                  </span>
                </div>

                <div className="flex-1">
                  <p className="font-medium text-[#0A1628]">{rep.first_name} {rep.last_name}</p>
                  <p className="text-sm text-gray-500">{rep.email}</p>
                </div>

                <div className="text-right">
                  {rep.chat_availability && Object.keys(rep.chat_availability).length > 0 ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Clock className="w-4 h-4" />
                      <span>
                        {Object.entries(rep.chat_availability)
                          .filter(([_, v]) => v?.enabled)
                          .length} days/week
                      </span>
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">No schedule set</span>
                  )}
                </div>

                {index === currentPosition && (
                  <span className="px-3 py-1 bg-[#84CC16] text-white text-xs font-medium rounded-full">
                    Next Up
                  </span>
                )}
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-xl">
            <div className="flex items-start gap-3">
              <MessageCircle className="w-5 h-5 text-[#014DB7] mt-0.5" />
              <div>
                <p className="text-sm font-medium text-[#014DB7]">How Round Robin Works</p>
                <p className="text-xs text-blue-600 mt-1">
                  New chat requests are automatically assigned to the "Next Up" rep in the queue. 
                  After assignment, the queue rotates to the next available rep. Drag reps to change the order.
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminSettingsPage;
